
import Vue from 'vue'
import dayjs from 'dayjs'
import fedHolidays from '@18f/us-federal-holidays'
import {
  formatDateTimeValue,
  formatDateValue,
  formatDisplayDate,
  formatDisplayTime,
  formatTimeValue,
} from '@/util/formatters'
import {
  VCalendar,
  VDivider,
  VForm,
  VList,
  VListItem,
  VListItemTitle,
  VMenu,
  VSheet,
  VSpacer,
  VTextarea,
  VToolbar,
  VToolbarTitle,
} from 'vuetify/lib'
import JcdcBtn from '@/components/jcdc/JcdcBtn.vue'
import JcdcCheckbox from '@/components/jcdc/JcdcCheckbox.vue'
import JcdcDatePicker from '@/components/jcdc/JcdcDatePicker.vue'
import JcdcFormGroup from '@/components/jcdc/JcdcFormGroup.vue'
import JcdcModal from '@/components/jcdc/JcdcModal.vue'
import JcdcSelect from '@/components/jcdc/JcdcSelect.vue'
import JcdcTextField from '@/components/jcdc/JcdcTextField.vue'
import JcdcTimePicker from '@/components/jcdc/JcdcTimePicker.vue'
import JcdcLabeledText from '@/components/jcdc/JcdcLabeledText.vue'

interface eventsType {
  name: string
  start: string
  end?: string
  location?: string
  details?: string
  calendar?: string
  allDay?: boolean
}
interface calType {
  name: string
  color: string
  edit: boolean
}

export default Vue.extend({
  name: 'JcdcCalendar',
  components: {
    JcdcBtn,
    JcdcCheckbox,
    JcdcDatePicker,
    JcdcFormGroup,
    JcdcModal,
    JcdcSelect,
    JcdcTextField,
    JcdcTimePicker,
    JcdcLabeledText,
    VCalendar,
    VDivider,
    VForm,
    VList,
    VListItem,
    VListItemTitle,
    VMenu,
    VSheet,
    VSpacer,
    VTextarea,
    VToolbar,
    VToolbarTitle,
  },
  props: {
    display: {
      type: String,
      default: 'month',
    },
    hideTitleBar: {
      type: Boolean,
      default: false,
    },
    hideNavigation: {
      type: Boolean,
      default: false,
    },
    events: {
      type: Array as () => Array<eventsType>,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    calendarList: {
      type: Array as () => Array<calType>,
      default: () => {
        return [
          {
            name: 'JCDC Calendar',
            color: 'primary',
            edit: true,
          },
        ]
      },
    },
    holidayUs: {
      type: Boolean,
      default: false,
    },
    height: {
      type: Number,
      default: 500,
    },
    weekdays: {
      type: Array as () => number[],
      default() {
        return [0, 1, 2, 3, 4, 5, 6]
      },
    },
  },
  data() {
    return {
      add: false,
      changeEvent: {} as eventsType,
      edit: false,
      editableEvent: true,
      remove: false,
      focus: '',
      fullCalendarList: [...this.calendarList] as Array<calType>,
      fullEventList: [...this.events] as Array<eventsType>,
      holidayCalendar: [
        {
          name: 'Holiday Calendar',
          color: 'error',
          edit: false,
        },
      ],
      modalColor: '',
      ready: false,
      selectedElement: null,
      selectedEndDate: '',
      selectedEndTime: '',
      selectedEvent: {} as eventsType,
      selectedIndex: -1,
      selectedOpen: false,
      selectedStartDate: '',
      selectedStartTime: '',
      type: this.display,
      typeToLabel: {
        month: 'Month',
        week: 'Week',
        day: 'Day',
        '4day': '4 Days',
      },
      resetEvent: {
        name: '',
        start: '',
        end: '',
        location: '',
        details: '',
        calendar: '',
        allDay: false,
      },
      valid: false,
    }
  },
  computed: {
    // Check the calendar is ready
    cal() {
      if (this.ready) {
        return this.$refs.calendar
      }
      return null
    },
    // Get current time
    currentTime() {
      return dayjs()
    },

    // Pull the list of editable calendars
    editableCalendarList(): Array<calType> {
      return this.calendarList.filter((cal) => cal.edit === true)
    },
    USHolidays() {
      const date = new Date(),
        thisYear = date.getFullYear(),
        nextYear = thisYear + 1,
        options = { shiftSaturdayHolidays: true, shiftSundayHolidays: true },
        holidays = [
          ...fedHolidays.allForYear(thisYear, options),
          ...fedHolidays.allForYear(nextYear, options),
        ]
      return holidays.map((item) => {
        return {
          name: item.name,
          start: item.dateString,
          end: '',
          location: '',
          details: '',
          calendar: 'Holiday Calendar',
          allDay: true,
        }
      })
    },
  },
  watch: {
    // Run full close event function when modal closes (in case upper close button is clicked)
    selectedOpen(val) {
      if (!val) {
        this.closeEvent()
      }
    },
  },
  async mounted() {
    // Set calendar to ready
    this.ready = true
    // Scroll to current time
    this.scrollToTime(this.currentTime.subtract(10, 'm').format('HH:MM'))
    // Force re-render, otherwise the header is missing
    this.fullEventList = [...this.events]
    // Add holiday events
    if (this.holidayUs) this.holidayCal()
    // Add accessibility updates
    this.a11yUpdates()
    if (document.querySelectorAll('.v-calendar-daily__body').length) {
      ;(
        document.querySelectorAll('.v-calendar-daily__body')[0] as HTMLElement
      ).style.maxHeight = '480px'
    }
    // Needed for testing
    await new Promise((resolve) => setTimeout(resolve))
  },
  updated() {
    const earliestTime = this.events.reduce((a, b) => {
        return formatTimeValue(a.start) < formatTimeValue(b.start) ? a : b
      }),
      formatEarliestTime = dayjs(earliestTime.start)
        .subtract(15, 'minutes')
        .format('HH:mm')
    // Scroll to day start time
    this.scrollToTime(formatEarliestTime)
    this.a11yUpdates()
  },
  methods: {
    formatDisplayTime,
    formatDisplayDate,
    // Calendar controls
    prev() {
      ;(this.cal as Vue & { prev: () => boolean }).prev()
    },
    next() {
      ;(this.cal as Vue & { next: () => boolean }).next()
    },
    // Scroll to set time
    scrollToTime(time) {
      ;(
        this.cal as Vue & {
          scrollToTime(x): () => string
        }
      ).scrollToTime(time)
    },
    // View day details when calendar date or "more" are clicked
    viewDay({ date }) {
      this.focus = date
      this.type = 'day'
    },
    // Triggered when event is clicked
    showEvent({ nativeEvent, event, eventParsed }) {
      const open = () => {
        // Event details
        this.selectedEvent = event
        this.selectedElement = nativeEvent.target
        this.selectedIndex = eventParsed.index
        // How to render modal
        this.modalColor = this.lookupCal(event, 'color')
        this.editableEvent = this.lookupCal(event, 'edit')
        // How to display dates
        this.parseDates(this.selectedEvent)
        // Open modal
        setTimeout(() => {
          this.selectedOpen = true
        }, 10)
      }
      if (this.selectedOpen) {
        // If open, close
        this.selectedOpen = false
        setTimeout(open, 10)
      } else {
        // Else open
        open()
      }
      nativeEvent.stopPropagation()
    },
    addEventClick({ date, time }) {
      if (this.editable) {
        this.selectedStartDate = date
        if (!time) {
          this.changeEvent.allDay = true
        } else {
          this.changeEvent.allDay = false
          // Set end date to start date
          this.selectedEndDate = date
          // Limit time to half hours
          const [hr, mi] = time.split(':'),
            endHr = parseInt(hr) + 1,
            min30 = (Math.floor(mi / 30) * 30).toLocaleString('en-US', {
              minimumIntegerDigits: 2,
            })
          // Set start/end times
          this.selectedStartTime = `${hr}:${min30}`
          this.selectedEndTime = `${endHr}:${min30}`
        }
        // Activate modal
        this.modalColor = 'primary'
        this.add = true
        this.selectedOpen = true
      }
    },
    // Display Date & Time in separate fields for editing
    parseDates(event) {
      this.selectedStartDate = formatDateValue(event.start)
      this.selectedStartTime =
        event.start.length > 10 ? formatTimeValue(event.start) : ''
      this.selectedEndDate = event.end ? formatDateValue(event.end) : ''
      this.selectedEndTime =
        event.end && event.end.length > 10 ? formatTimeValue(event.end) : ''
    },
    // Format date after change event
    formatDate(date, time?) {
      if (!dayjs(date).isValid()) return ''
      if (time) return formatDateTimeValue(`${date} ${time}`)
      return formatDateValue(date)
    },
    // Triggered when Save button is clicked
    saveEvent() {
      // Validate form
      ;(this.$refs.form as Vue & { validate: () => boolean }).validate()
      // If valid
      if (this.valid) {
        // Remove edited event from event list
        if (this.edit) {
          this.fullEventList.splice(this.selectedIndex, 1)
        }
        // Add event to event list
        this.fullEventList = [...this.fullEventList, { ...this.changeEvent }]
        this.$emit('change', this.fullEventList)
        // Close modal
        this.closeEvent()
      }
    },
    // Triggered when Delete Button is clicked
    deleteEvent() {
      this.fullEventList.splice(this.selectedIndex, 1)
      this.closeEvent()
    },
    closeEvent() {
      this.selectedOpen = false
      this.edit = false
      this.add = false
      this.remove = false
      this.valid = true
      this.selectedStartDate = ''
      this.selectedStartTime = ''
      this.selectedEndDate = ''
      this.selectedEndTime = ''
      this.changeEvent = this.resetEvent
      this.selectedEvent = this.resetEvent
    },
    mapChangeEvent() {
      this.changeEvent = {
        name: this.selectedEvent.name,
        start: this.selectedEvent.start,
        end: this.selectedEvent.end,
        location: this.selectedEvent.location,
        details: this.selectedEvent.details,
        calendar: this.selectedEvent.calendar,
        allDay: !this.selectedStartTime,
      }
    },
    lookupCal(event, lookup) {
      let result
      if (event.calendar) {
        result = this.fullCalendarList.find((x) => x.name === event.calendar)
      } else {
        result = this.calendarList[0]
      }
      if (lookup === 'edit') return result.edit
      return result.color
    },
    holidayCal() {
      // Add holiday events to event list
      this.fullEventList = [...this.events, ...this.USHolidays]
      // Add holiday calender to calendar list
      this.fullCalendarList = [...this.calendarList, ...this.holidayCalendar]
    },
    a11yUpdates() {
      // A11Y
      // Hide hidden button from screenReaders
      const hiddenBtn = document.querySelectorAll(
        'div[role="button"][aria-haspopup="true"]'
      )
      hiddenBtn.forEach((x) => {
        x.setAttribute('aria-label', 'hidden button trigger')
      })
      document.querySelectorAll('div.v-calendar-daily__head').forEach((x) => {
        x.setAttribute('role', 'row')
      })
      document
        .querySelectorAll('div.v-calendar-daily_head-day')
        .forEach((x) => {
          x.setAttribute('role', 'cell')
        })
      document.querySelectorAll('div.v-calendar-daily__body').forEach((x) => {
        x.setAttribute('role', 'row')
      })
      document.querySelectorAll('div.v-calendar-daily__day').forEach((x) => {
        x.setAttribute('role', 'cell')
      })
    },
  },
})
